import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Quote, Star } from 'lucide-react';
interface Testimonial {
  quote: string;
  author: string;
  role?: string;
}
const testimonials: Testimonial[] = [{
  quote: "PSI has already had a meaningful impact on how I think. I can honestly say I have not regretted the decision for a single second. PSI is different. It's not just giving information — it's training how to think. The quality of the community and access to expert thinking has been the most valuable part so far.",
  author: 'Johnson E.'
}, {
  quote: 'I first found PSI via a YouTube video in late January. I immediately got Peptide Salvation and was very impressed. My hat is off to you for translating the 700+ Khavinson articles. Even the commonly known research peptides are not mentioned in the Mayo regenerative medicine research we are doing, much less the Khavinson peptides that only you gentlemen have a grasp on.',
  author: 'Jeff S.',
  role: 'Regenerative Medicine Researcher'
}, {
  quote: "The two sessions were very enlightening! Even after 4 hours, it didn't feel like they were in a hurry to end! What made me realize this was different is they think outside the box and make each recommendation based on individual circumstances rather than a cookie cutter approach.",
  author: 'Michael E.'
}, {
  quote: 'Just grateful for you and what you are doing. Learning that you do not have to cycle Vilon, Vesugen, Pinealon and Epithalon like all the so-called experts said — this is BIG. Dosing protocols and how off others are is actually concerning. You have a lifer!',
  author: 'Brandon G.'
}, {
  quote: 'The fact that you are all deeply familiar with the Russian protocols is what I find most attractive to study under you guys. This was a great experience for me.',
  author: 'Clay A.'
}, {
  quote: 'No pretentious fluff. People asking the same questions I have. All answered one at a time. I liked it.',
  author: 'Cary S.'
}];
export function ProTestimonials() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-24 sm:py-32 px-4 sm:px-6 bg-black overflow-hidden" data-id="element-4124">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-600/5 rounded-full blur-3xl pointer-events-none" data-id="element-4125" />

      <motion.div ref={ref} initial={{
      opacity: 0
    }} animate={inView ? {
      opacity: 1
    } : {}} transition={{
      duration: 0.8
    }} className="relative max-w-6xl mx-auto" data-id="element-4126">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16" data-id="element-4127">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-4128">
            <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-500 to-amber-500" data-id="element-4129" />
            <div className="w-3 h-3 bg-amber-500 rotate-45" data-id="element-4130" />
            <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-amber-500 to-amber-500" data-id="element-4131" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-stone-100 mb-4 leading-tight max-w-5xl mx-auto" style={{
          fontFamily: 'Cinzel, serif',
          textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
        }} data-id="element-4132">
            The Transformations, Insights and Revelations Are{' '}
            <span className="text-amber-400" style={{
            textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
          }} data-id="element-4133">
              REAL
            </span>{' '}
            — And They're Happening Right Now Inside the Private Research Order
          </h2>
        </div>

        {/* Testimonial Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6" data-id="element-4134">
          {testimonials.map((testimonial, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08 * i
        }} className="relative p-6 sm:p-8 bg-gradient-to-br from-stone-950/60 to-black border border-amber-900/25 rounded-xl" data-id="element-4135">
              {/* Quote icon */}
              <Quote className="w-7 h-7 text-amber-600/30 mb-4" data-id="element-4136" />

              {/* Stars */}
              <div className="flex gap-1 mb-4" data-id="element-4137">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-500 text-amber-500" data-id="element-4138" />)}
              </div>

              {/* Quote text */}
              <p className="text-stone-300 leading-relaxed text-sm sm:text-base mb-6" data-id="element-4139">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="border-t border-amber-900/30 pt-4" data-id="element-4140">
                <p className="font-bold text-stone-100 text-sm sm:text-base" data-id="element-4141">
                  {testimonial.author}
                </p>
                {testimonial.role && <p className="text-xs sm:text-sm text-amber-500/70 mt-0.5" data-id="element-4142">
                    {testimonial.role}
                  </p>}
              </div>
            </motion.div>)}
        </div>

        {/* Bottom note & CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="mt-12 sm:mt-16 text-center" data-id="element-4143">
          <button onClick={() => {
          const pricingSection = document.querySelector('[data-pricing-section]');
          if (pricingSection) {
            pricingSection.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px] mb-8" data-id="element-4144">
            Join the Private Research Order Now
          </button>
        </motion.div>
      </motion.div>
    </section>;
}